/**
 * The wire contract for the operator console.
 *
 * Split from `shared/api.ts` rather than added to it, for a reason that is about blast radius
 * rather than tidiness: `api.ts` is imported by the SPA's main bundle, and every type in it is one
 * the ordinary app might reasonably use. Nothing in *this* file should ever appear in the editor
 * build. Keeping them apart means an accidental import of `AdminUserRow` into a UI module is a
 * visible line in a diff rather than an invisible one.
 *
 * Same rules as `api.ts`: types and constants only, no runtime behaviour, `.js` specifiers on
 * cross-imports so one module compiles under both the browser and the server tsconfig.
 *
 * ## What is deliberately not here
 *
 * There is no shape for reading a user's OpenAI key, because there is no route that returns one.
 * The console reports `hasOwnKey` and the `sk-…a91f` hint that is stored beside the ciphertext, and
 * that is the whole of what an operator gets. An admin surface is the most likely place for such a
 * route to be added "just for debugging", so the absence is stated rather than left to be noticed.
 */

import type { ProjectStats } from './api.js';
import type { PlanId } from './plans.js';

/** Path prefix for the operator console's API. Refused with a 404 for everyone not on the list. */
export const ADMIN_PREFIX = '/api/admin';

/** Where the console itself is served. */
export const ADMIN_PATH = '/admin';

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

/**
 * Deployment totals for the current calendar month.
 *
 * Revenue and margin are computed from plan *counts* against the prices in `shared/plans.ts`, not
 * from anything billing-shaped — there is no billing yet, and pretending otherwise would produce a
 * number that looks banked. It is what the accounts on the books would bring in at list price if
 * every one of them were paying, which until phase 8 is a statement about the plan column and
 * nothing more.
 */
export interface AdminOverview {
  /** Calendar month these figures cover, `YYYY-MM`. */
  period: string;
  accounts: number;
  /** Accounts that made at least one AI call this period. */
  activeAccounts: number;
  /** Accounts on each plan, keyed by id. Always carries every plan, including the empty ones. */
  planCounts: Record<PlanId, number>;
  /** Billable AI calls this period. Own-key calls are excluded throughout. */
  aiCalls: number;
  /** Estimated provider cost this period, in dollars. Never an invoice — see `shared/ai/pricing.ts`. */
  estimatedCostUsd: number;
  /** Credits consumed this period. */
  creditsUsed: number;
  /** Credits currently available through unexpired grants. */
  creditsGranted: number;
  /** List-price monthly revenue implied by the plan column. */
  revenueUsd: number;
  /** `(revenue − cost) / revenue`, or null when there is no revenue to take a fraction of. */
  grossMargin: number | null;
  /**
   * What the deployment is holding and who is on it right now.
   *
   * Deliberately not folded into the figures above. Everything before this point is about the
   * calendar month and moves when the month rolls over; these four are about *now* and do not. A
   * card that reset to zero on the first of the month because it had been grouped with the spend
   * figures would be a bug nobody noticed until they acted on it.
   */
  estate: AdminEstate;
  /** Whether AI is switched off deployment-wide, and why. */
  killSwitch: AdminKillSwitch;
}

/** Standing totals: not period figures, and they do not reset when the month does. */
export interface AdminEstate {
  /** Saved sites across every account, starters included. */
  sites: number;
  /** Sum of `projects.size_bytes` — serialized documents, not uploaded imagery. */
  siteBytes: number;
  /** Sessions that have not expired. One person on three devices is three. */
  liveSessions: number;
  /** Accounts holding at least one live session. */
  signedInAccounts: number;
}

export interface AdminKillSwitch {
  engaged: boolean;
  /** `environment` cannot be cleared from the console; `flag` can. Null when it is not engaged. */
  source: 'environment' | 'flag' | null;
  reason: string | null;
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

/** How the user table is ordered. Spend first, because the top of that list is the row that matters. */
export type AdminUserSort = 'spend' | 'credits' | 'recent' | 'name';

/** One row of the user table. */
export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  plan: PlanId;
  /** Where the allowance came from — `override` and `deployment` are worth seeing at a glance. */
  creditSource: 'plan' | 'override' | 'deployment';
  creditsUsed: number;
  /** Null means unmetered, which is not the same as nothing left. */
  creditsRemaining: number | null;
  creditsAllowed: number | null;
  estimatedCostUsd: number;
  costCapUsd: number;
  /** Spend as a fraction of the cost ceiling. 1 means the account is at or over it. */
  costCapFraction: number;
  aiSuspended: boolean;
  hasOwnKey: boolean;
  /** ISO timestamp. */
  lastSeenAt: string;
}

export interface AdminUserListResponse {
  users: AdminUserRow[];
  /** Total matching the filter, so the console can say "showing 50 of 812". */
  total: number;
}

/** One AI call, as the console shows it. */
export interface AdminUsageRow {
  createdAt: string;
  operation: string;
  model: string;
  billable: boolean;
  status: number;
  inputTokens: number | null;
  cachedTokens: number | null;
  outputTokens: number | null;
  estimatedCostUsd: number | null;
  credits: number | null;
  durationMs: number | null;
}

/** One row of `credit_grants`, including the spent and cancelled ones. */
export interface AdminGrantRow {
  id: string;
  credits: number;
  reason: string;
  /** Null when the granting admin's own account has since been deleted. */
  grantedByEmail: string | null;
  /** Null means it renews every month until cancelled — see the migration. */
  expiresAt: string | null;
  /** False once `expires_at` has passed, which is also how a grant is cancelled. */
  active: boolean;
  createdAt: string;
}

/** One row of `admin_audit`, as the console shows it. */
export interface AdminAuditRow {
  id: string;
  createdAt: string;
  adminEmail: string;
  action: string;
  targetUserId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
}

export interface AdminUserDetail {
  user: AdminUserRow;
  /** The redacted `sk-…a91f` hint, when they have supplied a key. Never the key. */
  ownKeyHint: string | null;
  createdAt: string;
  planSince: string;
  creditOverride: number | null;
  costCapOverride: number | null;
  /** Most recent first, capped — this is a console, not an export. */
  usage: AdminUsageRow[];
  grants: AdminGrantRow[];
  audit: AdminAuditRow[];
  /** Live sessions for this account. Empty means signed out everywhere, not never signed in. */
  sessions: AdminSessionRow[];
  /** Sites this account is holding, newest first, capped like everything else here. */
  sites: AdminSiteRow[];
  /** Sites and bytes in total, which the capped list above cannot be counted to get. */
  siteCount: number;
  siteBytes: number;
}

// ---------------------------------------------------------------------------
// Breakdown
// ---------------------------------------------------------------------------

/** Cost and volume for one operation or one model this period. */
export interface AdminBreakdownRow {
  key: string;
  calls: number;
  estimatedCostUsd: number;
  creditsCharged: number;
  /** Sum of `input_tokens`, or null for image operations that have none. */
  inputTokens: number | null;
  cachedTokens: number | null;
  outputTokens: number | null;
  /** `cachedTokens / inputTokens`, which is the number phase 7 exists to move. Null when unknowable. */
  cacheHitRate: number | null;
}

export interface AdminBreakdownResponse {
  period: string;
  byOperation: AdminBreakdownRow[];
  byModel: AdminBreakdownRow[];
}

// ---------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------

/** How the site table is ordered. */
export type AdminSiteSort = 'updated' | 'created' | 'size' | 'plants' | 'name';

/**
 * One saved site, as the console lists it.
 *
 * Metadata only. The document is a separate route, and fetching it is an audited act — see
 * {@link AdminSiteDocument}.
 */
export interface AdminSiteRow {
  /** Project ids are client-generated and scoped per account, so this pair is the real key. */
  userId: string;
  id: string;
  name: string;
  ownerEmail: string;
  ownerName: string;
  sizeBytes: number;
  /** Null for a row saved before stats were recorded. Renders as dashes rather than zeros. */
  stats: ProjectStats | null;
  /** Handed to the account at signup rather than made in it. */
  isStarter: boolean;
  createdAt: string;
  updatedAt: string;
  /** Null means never opened since `opened_at` shipped, which is not the same as never opened. */
  openedAt: string | null;
}

export interface AdminSiteListResponse {
  sites: AdminSiteRow[];
  total: number;
  /** Sum of `size_bytes` across everything matching the filter, not just the page shown. */
  totalBytes: number;
}

/**
 * One site's stored document, for an operator who has to look inside it.
 *
 * This is the only route in the console that returns another account's private content, and it is
 * the only *read* that writes an `admin_audit` row. The asymmetry is on purpose: every other read
 * here is a number about an account, and this one is the account's work. If an operator opens a
 * customer's garden design, that should be a fact somebody can later discover, in the same log and
 * with the same required reason as a credit grant.
 *
 * The console offers it as a `.landscape.json` download because the editor already imports that
 * format — so looking at a site properly costs no new viewer, and the copy that gets opened is
 * unmistakably a copy.
 */
export interface AdminSiteDocument {
  site: AdminSiteRow;
  /** The stored `LandscapeProject`, opaque to the server and to this type. */
  document: unknown;
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

/**
 * One live sign-in.
 *
 * There is no separate login-event table, and this doubles as the sign-in history: a row is written
 * when somebody signs in and lives until it expires or is revoked. Two consequences the console
 * states rather than hides — an explicit sign-out erases its own row, and the pruner deletes
 * expired ones on a timer, so this reaches back at most `SESSION_TTL_DAYS` and only over sessions
 * nobody ended.
 */
export interface AdminSessionRow {
  userId: string;
  email: string;
  name: string;
  /** Never the token, and never its hash in full — a short prefix, enough to tell two rows apart. */
  ref: string;
  /** Raw `User-Agent`, trimmed. Parsing it into "Chrome on macOS" is the client's business. */
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  expiresAt: string;
  /** True for the session the operator is reading this page with. Never offer to revoke your own. */
  isCurrent: boolean;
}

export interface AdminSessionListResponse {
  sessions: AdminSessionRow[];
  total: number;
  /** Distinct accounts among the matching rows, which is the number "who is online" means. */
  accounts: number;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Every write route carries a reason, and the server refuses one that is blank.
 *
 * "Because they asked" is a perfectly good reason. No reason at all is not, and a required field is
 * the cheapest prompt to think that exists — it costs the operator four words and buys whoever
 * reads the audit log in six months the only context that was ever available.
 */
export interface AdminReasoned {
  reason: string;
}

export interface AdminPlanRequest extends AdminReasoned {
  plan: PlanId;
}

export interface AdminGrantRequest extends AdminReasoned {
  credits: number;
  /**
   * When the grant stops applying, or explicitly `null` for one that never does.
   *
   * Required rather than optional, and that is the entire point of the field. A grant with no
   * expiry raises the account's allowance *every month* until somebody cancels it, which is right
   * for honouring a paid plan by hand before billing exists and wrong for a one-off boost. Making
   * the caller state which one they mean turns a forgotten field into a decision.
   */
  expiresAt: string | null;
}

export interface AdminOverridesRequest extends AdminReasoned {
  /** Replaces the plan's monthly credits outright. Null clears it. */
  creditOverride: number | null;
  /** Dollars per month this account may cost to serve. Null falls back to the plan's. */
  costCapOverride: number | null;
}

export interface AdminSuspendRequest extends AdminReasoned {
  suspended: boolean;
}

export interface AdminFlagRequest extends AdminReasoned {
  engaged: boolean;
}

/**
 * Revoke every session an account holds — "sign them out everywhere".
 *
 * No body beyond the reason, and deliberately not a toggle: there is no "sign them back in". It is
 * the right lever for a reported stolen laptop or a shared password, and it is the wrong lever for
 * cutting off abuse, which is what {@link AdminSuspendRequest} is for. A revoked account can sign
 * straight back in.
 */
export type AdminSignOutRequest = AdminReasoned;
